import { useEffect, useState } from 'react'

function getOnlineStatus(): boolean {
  if (typeof navigator === 'undefined') {
    return true
  }
  return navigator.onLine
}

async function probeOriginReachable(options?: { timeoutMs?: number }): Promise<boolean> {
  if (typeof window === 'undefined') {
    return true
  }

  const timeoutMs = options?.timeoutMs ?? 2500
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    // IMPORTANT: Use a resource that is NOT precached by the service worker.
    // `public/ping.txt` is intentionally `.txt` so it won't match Workbox precache globs.
    const url = `/ping.txt?ts=${Date.now()}`
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        'cache-control': 'no-cache',
        pragma: 'no-cache',
      },
    })
    // Any HTTP response means we reached the origin (even 404/500).
    return response.status > 0
  } catch {
    return false
  } finally {
    window.clearTimeout(timeout)
  }
}

type OnlineListener = (value: boolean) => void

const listeners = new Set<OnlineListener>()
const monitor = {
  started: false,
  intervalId: null as number | null,
  inFlight: false,
  online: getOnlineStatus(),
  lastProbeLatencyMs: null as number | null,
}

type LatencyListener = (value: number | null) => void
const latencyListeners = new Set<LatencyListener>()

function notifyLatencyListeners(): void {
  for (const listener of latencyListeners) {
    listener(monitor.lastProbeLatencyMs)
  }
}

function notifyListeners(): void {
  for (const listener of listeners) {
    listener(monitor.online)
  }
}

function setSharedOnline(value: boolean): void {
  if (monitor.online === value) {
    return
  }
  monitor.online = value
  notifyListeners()
}

async function runProbe(): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }
  if (monitor.inFlight) {
    return
  }
  monitor.inFlight = true
  try {
    const start = performance.now()
    const reachable = await probeOriginReachable()
    const elapsed = performance.now() - start
    monitor.lastProbeLatencyMs = reachable ? Math.round(elapsed) : null
    notifyLatencyListeners()
    setSharedOnline(reachable)
  } finally {
    monitor.inFlight = false
  }
}

function handleOnline(): void {
  // Optimistically set true, then confirm reachability.
  setSharedOnline(true)
  void runProbe()
}

function handleOffline(): void {
  // Optimistically set false, but still allow probe to correct if needed.
  setSharedOnline(false)
}

function handleVisibilityOrFocus(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    void runProbe()
  }
}

function startMonitorIfNeeded(): void {
  if (monitor.started || typeof window === 'undefined') {
    return
  }

  monitor.started = true
  void runProbe()

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('focus', handleVisibilityOrFocus)
  window.addEventListener('pageshow', handleVisibilityOrFocus)
  document.addEventListener('visibilitychange', handleVisibilityOrFocus)

  // Periodic probe while visible (battery-friendly).
  monitor.intervalId = window.setInterval(() => {
    if (typeof document === 'undefined') return
    if (document.visibilityState !== 'visible') return
    void runProbe()
  }, 15000)
}

function stopMonitorIfIdle(): void {
  if (!monitor.started || listeners.size > 0 || typeof window === 'undefined') {
    return
  }

  monitor.started = false

  if (monitor.intervalId !== null) {
    window.clearInterval(monitor.intervalId)
    monitor.intervalId = null
  }

  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  window.removeEventListener('focus', handleVisibilityOrFocus)
  window.removeEventListener('pageshow', handleVisibilityOrFocus)
  document.removeEventListener('visibilitychange', handleVisibilityOrFocus)
}

function subscribeOnlineStatus(listener: OnlineListener): () => void {
  listeners.add(listener)
  startMonitorIfNeeded()
  listener(monitor.online)

  return () => {
    listeners.delete(listener)
    stopMonitorIfIdle()
  }
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => monitor.online)

  useEffect(() => {
    return subscribeOnlineStatus(setOnline)
  }, [])

  return online
}

export function useProbeLatency(): number | null {
  const [latency, setLatency] = useState(() => monitor.lastProbeLatencyMs)

  useEffect(() => {
    latencyListeners.add(setLatency)
    setLatency(monitor.lastProbeLatencyMs)
    startMonitorIfNeeded()
    return () => {
      latencyListeners.delete(setLatency)
    }
  }, [])

  return latency
}

export function getLastProbeLatencyMs(): number | null {
  return monitor.lastProbeLatencyMs
}
